/**
 * POST a JSON payload to a backend endpoint and return its `data` object.
 *
 * @param {string} endpoint Path under API_BASE_URL.
 * @param {object} payload Request body.
 * @param {string|null} accessToken Bearer token, omitted from the headers when absent.
 * @returns {Promise<object>} The `data` object from a successful response.
 * @throws {Error} With a `code` field on any non-success response.
 */
import { API_BASE_URL } from '../constants';

const REQUEST_TIMEOUT_MS = 30000;

const send = async (endpoint, payload, accessToken) => {
  const url = `${API_BASE_URL}${endpoint}`;
  console.log('[api] POST', url, { payload, hasToken: Boolean(accessToken) });
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), REQUEST_TIMEOUT_MS);

  try {
    const response = await fetch(url, {
      method: 'POST',
      signal: controller.signal,
      headers: {
        'Content-Type': 'application/json',
        ...(accessToken ? { Authorization: `Bearer ${accessToken}` } : {}),
      },
      body: JSON.stringify(payload),
    });

    // Read as text first: gateway errors (502) return HTML, and response.json()
    // would throw a parse error that hides the real status.
    const raw = await response.text();
    console.log('[api] response', response.status, response.statusText, raw.slice(0, 1000));

    let body = null;
    try {
      body = JSON.parse(raw);
    } catch (parseErr) {
      const error = new Error(`Server returned ${response.status} with a non-JSON response.`);
      error.code = `HTTP_${response.status}`;
      throw error;
    }

    if (!response.ok || body.success !== true) {
      const error = new Error(body.error?.message || `Request failed (${response.status}).`);
      error.code = body.error?.code || `HTTP_${response.status}`;
      throw error;
    }

    return body.data ?? {};
  } catch (err) {
    if (err.name === 'AbortError') {
      err.message = `Request timed out after ${REQUEST_TIMEOUT_MS / 1000} seconds.`;
      err.code = 'REQUEST_TIMEOUT';
    }
    console.error('[api] request failed', { url, payload, code: err.code, message: err.message });
    const error = new Error(err.message || 'Network error occurred.');
    error.code = err.code || 'NETWORK_ERROR';
    throw error;
  } finally {
    clearTimeout(timeoutId);
  }
};

const isExpired = (error) => error.code === 'INVALID_TOKEN' || error.code === 'HTTP_401';

/**
 * Renew an expired access token. Set by AuthProvider; returns the fresh token,
 * or null when the refresh token is gone or itself rejected.
 *
 * It must not route through post(): a refresh raised from inside the queue
 * would wait on the queue's own tail and deadlock.
 */
let renewToken = null;

export const setTokenRenewer = (renewer) => {
  renewToken = renewer;
};

// The backend answers 502 when requests reach it at once, even when they hit
// different endpoints. Keep one global queue so the browser behaves like
// Postman/Hopscotch: one API call at a time. The timeout above prevents one bad
// request from leaving every later screen stuck behind it forever.
let queue = Promise.resolve();

export const post = (endpoint, payload, accessToken) => {
  const result = queue.then(async () => {
    try {
      return await send(endpoint, payload, accessToken);
    } catch (err) {
      // One retry only: a second 401 means the refresh token is finished too,
      // and the error travels on so AuthProvider can end the session.
      if (!isExpired(err) || !renewToken) throw err;
      const renewed = await renewToken();
      if (!renewed) throw err;
      console.log('[api] token renewed, retrying', endpoint);
      return send(endpoint, payload, renewed);
    }
  });
  queue = result.catch(() => {});
  return result;
};
